import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import HomeHeader from "../../components/header/HomeHeader";
import CommonFooter from "../../components/footer/CommonFooter";
import ProductCard from "../home/components/ProductCard";
import FullscreenLoader from "../../components/loader/fullScreenLoader/FullscreenLoader";
import { appApiHandler } from "../../api/appApiHandler";
import "./CategoryProduct.css";
import "../search/Search.css";
import { getUserType } from "../../constants/asyncStoreManager";
import { IoIosArrowDown } from "react-icons/io";
import { IoFilter, IoClose } from "react-icons/io5";
import Breadcrumb from "../../components/breadcrumb";
import EmptyState from "../../components/EmptyState";
import { FaShoppingBag } from "react-icons/fa";
import { getOptimizedUrl } from "../../constants/globalFunction";
import { Range } from "react-range";
import { colors } from "../../constants/colors";
import "../../styles/filterColorSwatches.css";

const PRICE_SLIDER_MIN = 0;
const PRICE_SLIDER_MAX = 50000;

const PRICE_UNDER_OPTIONS = [
  { value: 999, label: "Under ₹999" },
  { value: 1999, label: "Under ₹1999" },
  { value: 2999, label: "Under ₹2999" },
  { value: 4999, label: "Under ₹4999" },
  { value: 7999, label: "Under ₹7999" },
  { value: 14999, label: "Under ₹14999" },
  { value: 19999, label: "Under ₹19999" },
  { value: 24999, label: "Under ₹24999" }
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "price_low_to_high", label: "Price: Low to High" },
  { value: "price_high_to_low", label: "Price: High to Low" },
];

const CategoryProduct = () => {
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const urlCategoryId = urlParams.get("category_id");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fabrics, setFabrics] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);

  const [selectedSortOption, setSelectedSortOption] = useState("newest");

  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);

  const [categoryObj, setCategoryObj] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [priceRange, setPriceRange] = useState([
    PRICE_SLIDER_MIN,
    PRICE_SLIDER_MAX,
  ]);

  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isFabricsOpen, setIsFabricsOpen] = useState(false);
  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [isSizesOpen, setIsSizesOpen] = useState(false);
  const [isColorsOpen, setIsColorsOpen] = useState(false);

  const [showFilters, setShowFilters] = useState(false);

  const [tempFabrics, setTempFabrics] = useState([]);
  const [tempBrands, setTempBrands] = useState([]);
  const [tempSizes, setTempSizes] = useState([]);
  const [tempColors, setTempColors] = useState([]);

  const [selectedPriceUnder, setSelectedPriceUnder] = useState(null);
  const [tempPriceUnder, setTempPriceUnder] = useState(null);

  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);

  const listingViewState = location.state?.listingView;
  const incomingSize = location.state?.selectedSize;

  // =====================================================
  // GET FILTER PARAMS
  // =====================================================
  const getFilterParams = async () => {
    try {
      const response = await appApiHandler.getProductFilterParams();
      if (response?.status === 200) {
        if (response?.fabrics) setFabrics(response?.fabrics);
        if (response?.brands) setBrands(response?.brands);
        if (response?.sizes) setSizes(response?.sizes);
        if (response?.colors) setColors(response?.colors);
      }
    } catch (error) {
      console.log("FILTER PARAM ERROR", error);
    }
  };

  // =====================================================
  // GET PRODUCTS
  // =====================================================
  const getProducts = async () => {
    const userType = (await getUserType()) || "retailer";
    const categoryId = urlParams.get("category_id");

    const reqData = {
      user_type: userType,
      category_id: categoryId || "",
      fabrics: selectedFabrics || [],
      brands: selectedBrands || [],
      sizes: selectedSizes || [],
      colors: selectedColors || [],
      min_price: minPrice || "",
      max_price: maxPrice || "",
      ...(selectedPriceUnder != null ? { price_under: selectedPriceUnder } : {}),
      sort_type: selectedSortOption || "newest",
    };

    const response = await appApiHandler.filterProducts(reqData);

    if (response?.status === 200) {
      setCategoryObj(response?.category || null);
      const list = response?.data || [];
      setProducts(list);
      const rawTotal = response?.total_count ?? response?.total;
      const parsed = (rawTotal !== undefined && rawTotal !== null) ? Number(rawTotal) : list.length;
      setTotalCount(Number.isFinite(parsed) ? parsed : list.length);
    }
  };

  useEffect(() => {
    getFilterParams();
    if (incomingSize) {
      setSelectedSizes([incomingSize]);
      setTempSizes([incomingSize]);
    }
  }, [incomingSize]);

  useEffect(() => {
    setLoading(true);
    getProducts().finally(() => {
      setLoading(false);
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [
    urlCategoryId,
    selectedSortOption,
    selectedFabrics,
    selectedBrands,
    selectedSizes,
    selectedColors,
    minPrice,
    maxPrice,
    selectedPriceUnder,
  ]);

  const togglePriceUnder = (value) => {
    setTempPriceUnder((prev) => (prev === value ? null : value));
  };

  const toggleFromArray = (array, setter, value) => {
    const exists = array.includes(value);
    const next = exists ? array.filter((v) => v !== value) : [...array, value];
    setter(next);
  };

  const handleApplyAll = () => {
    setMinPrice(priceRange[0]);
    setMaxPrice(priceRange[1]);
    setSelectedFabrics(tempFabrics);
    setSelectedBrands(tempBrands);
    setSelectedSizes(tempSizes);
    setSelectedColors(tempColors);
    setSelectedPriceUnder(tempPriceUnder);
    setShowFilters(false);
  };

  const handleClearAll = () => {
    setTempFabrics([]);
    setTempBrands([]);
    setTempSizes([]);
    setTempColors([]);
    setTempPriceUnder(null);
    setMinPrice("");
    setMaxPrice("");
    setPriceRange([PRICE_SLIDER_MIN, PRICE_SLIDER_MAX]);
    setSelectedFabrics([]);
    setSelectedBrands([]);
    setSelectedSizes([]);
    setSelectedColors([]);
    setSelectedPriceUnder(null);
    setShowFilters(false);
  };

  const toggleFilters = () => {
    setShowFilters((open) => !open);
  };

  useEffect(() => {
    setTempFabrics(selectedFabrics);
    setTempBrands(selectedBrands);
    setTempSizes(selectedSizes);
    setTempColors(selectedColors);
    setTempPriceUnder(selectedPriceUnder);

    if (minPrice !== "" && maxPrice !== "") {
      setPriceRange([Number(minPrice), Number(maxPrice)]);
    } else {
      setPriceRange([PRICE_SLIDER_MIN, PRICE_SLIDER_MAX]);
    }
  }, [minPrice, maxPrice, selectedFabrics, selectedBrands, selectedSizes, selectedColors, selectedPriceUnder]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (!sortMenuRef.current?.contains(e.target)) setSortMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setSortMenuOpen(false);
    };
    if (sortMenuOpen) {
      document.addEventListener("mousedown", onDocDown);
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sortMenuOpen]);

  useEffect(() => {
    if (showFilters) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [showFilters]);

  const filterActions = (
    <div className="searchFiltersActions">
      <button type="button" className="searchBtnSecondary" onClick={handleClearAll}>
        Clear
      </button>
      <button type="button" className="searchBtnPrimary" onClick={handleApplyAll}>
        Apply
      </button>
    </div>
  );

  return (
    <div className="categoryProductPage">
      {/* <HomeHeader /> */}

      {/* HERO */}
      {(() => {
        // Prefer thumbnail over image; clean Laravel storage paths
        const rawBg = categoryObj?.thumbnail || categoryObj?.image;
        // const bgPath = rawBg
        //   ? rawBg
        //       .replace(/^storage\/app\/public\//, "storage/")
        //       .replace(/^public\//, "storage/")
        //       .replace(/^\/+/, "")
        //   : null;
          console.log("category thumb");
        //const bgUrl = bgPath ? getOptimizedUrl(bgPath) : null;
        const bgUrl = getOptimizedUrl(rawBg);
console.log(bgUrl);
        return (
          <section
            className={`categoryHero ${bgUrl ? "" : "categoryHero--placeholder"}`}
            aria-labelledby="categoryHeroTitle"
          >
            <div
              className="categoryHero__bg"
              style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
            />
            <div className="categoryHero__overlay" />
            <div className="categoryHero__breadcrumb">
              <Breadcrumb
                items={[
                  { label: "HOME", path: "/" },
                  { label: categoryObj?.name?.toUpperCase?.() || "SHOP", isActive: true },
                ]}
              />
            </div>
            <h1 id="categoryHeroTitle" className="categoryHero__title">
              {categoryObj?.name?.toUpperCase?.() || "SHOP"}
            </h1>
          </section>
        );
      })()}

      {/* TOOLBAR */}
      <div className="categoryCollectionToolbar">
        <div className="categoryCollectionToolbar__inner">
          <span className="categoryCollectionToolbar__edgeSpacer" aria-hidden />
          <p className="categoryCollectionToolbar__count" aria-live="polite">
            <span className="categoryCollectionToolbar__countValue">{totalCount}</span>
            <span className="categoryCollectionToolbar__countLabel"> PRODUCTS</span>
          </p>

          <div className="categoryCollectionToolbar__actions">
            {/* SORT */}
            <div className="categorySortDropdown" ref={sortMenuRef}>
              <button type="button" className="categorySortDropdown__trigger" aria-expanded={sortMenuOpen} onClick={() => setSortMenuOpen((open) => !open)}>
                Sort by
                <IoIosArrowDown size={18} className={sortMenuOpen ? "categorySortDropdown__chevron is-open" : "categorySortDropdown__chevron"} />
              </button>
              {sortMenuOpen && (
                <ul className="categorySortDropdown__list">
                  {SORT_OPTIONS.map(({ value, label }) => (
                    <li key={value}>
                      <button
                        type="button"
                        className={`categorySortDropdown__option${selectedSortOption === value ? " is-selected" : ""}`}
                        onClick={() => {
                          setSelectedSortOption(value);
                          setSortMenuOpen(false);
                        }}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* FILTER BUTTON ( only Shown in Mobile) */}
            <button
              type="button"
              className="categoryCollectionToolbar__filterBtn"
              onClick={() => setShowFilters(true)}
            >
              <IoFilter size={18} />
              <span>Filter</span>
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA WITH SIDEBAR */}
      <div className="page-content categoryPageContent">
        <main className="categoryProductPage__main categoryProductPage__main--withSidebar">

          {/* SIDEBAR FILTER COLUMN */}
          <aside className={`categoryProductSidebar ${showFilters ? "show" : ""}`}>
            <div className="searchFiltersAccordion">
              <div className="searchFiltersHeader">
                <span className="searchFiltersHeader__title">FILTERS</span>
                <button type="button" className="searchFiltersHeader__close" onClick={() => setShowFilters(false)}>
                  <IoClose size={24} />
                </button>
              </div>
              <div className="searchFiltersContent">
                {/* PRICE FILTER */}
                <div className="searchAccordionSection">
                  <button type="button" className="searchAccordionHeader" onClick={() => setIsPriceOpen((v) => !v)}>
                    <span>Price</span>
                    <IoIosArrowDown size={18} className={isPriceOpen ? "is-open" : ""} />
                  </button>
                  {isPriceOpen && (
                    <div className="searchAccordionBody">
                      <div className="searchFiltersBar__priceInputs">
                        <div className="searchPrice-slider-container">
                          <Range
                            step={1} min={0} max={50000}
                            values={priceRange}
                            onChange={(values) => setPriceRange(values)}
                            renderTrack={({ props, children }) => {
                              const { key, ...trackProps } = props;
                              return (
                                <div {...trackProps} key={key} className="searchPrice-slider-track">
                                  <div className="searchPrice-slider-range" style={{
                                    left: `${(priceRange[0] / 50000) * 100}%`,
                                    width: `${((priceRange[1] - priceRange[0]) / 50000) * 100}%`,
                                  }}
                                  />
                                  {children}
                                </div>
                              );
                            }}
                            renderThumb={({ props }) => {
                              const { key, ...thumbProps } = props;
                              return <div {...thumbProps} key={key} className="searchPrice-slider-thumb" />;
                            }}
                          />
                          <span className="searchPrice-range-text">Rs. {priceRange[0]} — Rs. {priceRange[1]}</span>

                          <div className="searchFilters__priceUnderGroup">
                            {PRICE_UNDER_OPTIONS.map(({ value, label }) => (
                              <button
                                key={value}
                                type="button"
                                className={`searchFilters__priceUnderBtn ${tempPriceUnder === value ? "is-selected" : ""}`}
                                onClick={() => togglePriceUnder(value)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* FABRICS */}
                {fabrics?.length > 0 && (
                  <div className="searchAccordionSection">
                    <button type="button" className="searchAccordionHeader" onClick={() => setIsFabricsOpen((v) => !v)}>
                      <span>Fabrics</span>
                      <IoIosArrowDown size={18} className={isFabricsOpen ? "is-open" : ""} />
                    </button>
                    {isFabricsOpen && (
                      <div className="searchAccordionBody">
                        <div className="searchFiltersBar__checkboxGrid">
                          {fabrics.map((f) => (
                            <label key={f} className="searchFiltersBar__checkboxItem">
                              <input type="checkbox" checked={tempFabrics.includes(f)} onChange={() => toggleFromArray(tempFabrics, setTempFabrics, f)} />
                              <span>{f}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* COLORS */}
                {colors?.length > 0 && (
                  <div className="searchAccordionSection">
                    <button type="button" className="searchAccordionHeader" onClick={() => setIsColorsOpen((v) => !v)}>
                      <span>Color</span>
                      <IoIosArrowDown size={18} className={isColorsOpen ? "is-open" : ""} />
                    </button>
                    {isColorsOpen && (
                      <div className="searchAccordionBody">
                        <div className="filtersColorSwatchGrid">
                          {colors.map((color) => {
                            const colorName = color?.name;
                            const swatch = color?.hex_code || "#000000";
                            const isSelected = tempColors.includes(colorName);
                            return (
                              <button key={color?._id || colorName} type="button" className={`filtersColorSwatch ${isSelected ? "is-selected" : ""}`} onClick={() => toggleFromArray(tempColors, setTempColors, colorName)}>
                                <span className="filtersColorSwatch__dot" style={String(swatch).includes("gradient") ? { background: swatch } : { backgroundColor: swatch }} />
                                <span className="filtersColorSwatch__label">{colorName}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {filterActions}
            </div>
          </aside>

          {/* BACKDROP for Mobile Sidebar */}
          {showFilters && (
            <div
              className="searchFiltersBackdrop show"
              onClick={() => setShowFilters(false)}
            />
          )}

          {/* RESULTS AREA */}
          <div className="categoryProductPage__contentArea">
            <section className="categoryProductPage__results">
              {products?.length > 0 ? (
                <div className="categoryProductPage__productGrid">
                  {products?.map((item) => (
                    <ProductCard key={item?._id} item={item} singleImage />
                  ))}
                </div>
              ) : (
                <EmptyState icon={FaShoppingBag} title="No products found in this category." iconSize={40} />
              )}
            </section>
          </div>
        </main>
      </div>

      <CommonFooter />
      {loading && <FullscreenLoader text="Loading..." />}
    </div>
  );
};

export default CategoryProduct;
